import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Clock, Building2, Euro } from 'lucide-react';
import { toast } from 'sonner';



function fmt(dateStr) {
  if (!dateStr) return '—';
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
  return d.toLocaleString('fr-FR', { 
    timeZone: 'Europe/Brussels',
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

  const sortPrestationsByDate = (data) => {
          return [...data].sort((a, b) => {
            const dateA = a.dateStart
              ? new Date(a.dateStart).getTime()
              : Infinity;

            const dateB = b.dateStart
              ? new Date(b.dateStart).getTime()
              : Infinity;

            return dateA - dateB;
          });
        };


export default function UserPrestations() {
  const { axiosInstance, user } = useAuth();
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [observations, setObservations] = useState({});
  const [exchangePrestationId, setExchangePrestationId] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [showExchangeModal, setShowExchangeModal] = useState(false);

  useEffect(() => {
    if (user) fetchPrestations();
  }, [user]);

  const fetchPrestations = async () => {
  setLoading(true);

 

  try {
    // ---------------------------------------------------------
    // 1. Charger les prestations
    // ---------------------------------------------------------
    // const res = await axiosInstance.get('/prestations');
    const res = await axiosInstance.get('/prestations/me');

    const rawData = res.data || [];

    // ---------------------------------------------------------
    // 2. Chercher les prestations terminées dans le temps
    //    mais qui n'ont pas encore leur gain_final correct
    // ---------------------------------------------------------
    const now = new Date();

    const prestationsToComplete = rawData.filter((p) => {
      if (!p._id) return false;
      if (!p.dateEnd) return false;

      const dateEnd = new Date(p.dateEnd);

      if (isNaN(dateEnd.getTime())) {
        return false;
      }

      const expired = dateEnd < now;

      const finalRate =
        p.final_rate != null
          ? Number(p.final_rate)
          : 0;

      const gainFinal =
        p.gain_final != null
          ? Number(p.gain_final)
          : null;

      // Prestation passée + final_rate disponible
      // + gain_final absent ou différent
      return (
        expired &&
        p.status !== 'completed' &&
        finalRate > 0 &&
        gainFinal !== finalRate
      );
    });

    // ---------------------------------------------------------
    // 3. Synchroniser les gains finaux
    // ---------------------------------------------------------
    if (prestationsToComplete.length > 0) {

      await Promise.all(
        prestationsToComplete.map(async (p) => {
          try {
            await handleAutoCompleteExpired(p._id);
          } catch (err) {
            console.error(
              `Erreur synchronisation ${p._id}`,
              err
            );
          }
        })
      );

      

      // -------------------------------------------------------
      // 4. Recharger les données après mise à jour
      // -------------------------------------------------------
      const refreshed =
        await axiosInstance.get('/prestations/me');

      const refreshedData = refreshed.data || [];

      const data = refreshedData.map((p) => ({
        _id: p._id,
        name: p.name || p.type || '—',
        description:
          p.description || p.notes || '—',

        dateStart: p.dateStart || null,
        dateEnd: p.dateEnd || null,

        dureeMinutes:
          p.dureeMinutes || 0,

        assigned_to:
          p.assigned_to ||
          p.soignant ||
          null,

        assigned_name:
          p.assigned_name || '—',

        etablissement:
          p.etablissement || null,

        etablissement_name:
          p.etablissement?.name ||
          p.institution_name ||
          '—',

        status:
          p.status ||
          p.statut ||
          'pending',

        started_at:
          typeof p.started_at === 'string'
            ? p.started_at
            : p.started_at?.toISOString(),

        ended_at:
          p.ended_at || null,

        actual_duration:
          p.actual_duration != null
            ? p.actual_duration
            : null,

        pricing_type:
          p.pricing_type,

        base_rate:
          p.base_rate,

        travel_fee:
          p.travel_fee,

        final_rate:
          p.final_rate,

        gain_estime:
          p.gain_estime ?? 0,

        gain_final:
          p.gain_final ?? null,

        observation:
          p.observation || null,
      }));

      // setPrestations(data);
      setPrestations(sortPrestationsByDate(data));

    } else {

      // -------------------------------------------------------
      // Aucune synchronisation nécessaire
      // -------------------------------------------------------

      const data = rawData.map((p) => ({
        _id: p._id,
        name: p.name || p.type || '—',
        description:
          p.description || p.notes || '—',

        dateStart: p.dateStart || null,
        dateEnd: p.dateEnd || null,

        dureeMinutes:
          p.dureeMinutes || 0,

        assigned_to:
          p.assigned_to ||
          p.soignant ||
          null,

        assigned_name:
          p.assigned_name || '—',

        etablissement:
          p.etablissement || null,

        etablissement_name:
          p.etablissement?.name ||
          p.institution_name ||
          '—',

        status:
          p.status ||
          p.statut ||
          'pending',

        started_at:
          typeof p.started_at === 'string'
            ? p.started_at
            : p.started_at?.toISOString(),

        ended_at:
          p.ended_at || null,

        actual_duration:
          p.actual_duration != null
            ? p.actual_duration
            : null,

        pricing_type:
          p.pricing_type,

        base_rate:
          p.base_rate,

        travel_fee:
          p.travel_fee,

        final_rate:
          p.final_rate,

        gain_estime:
          p.gain_estime ?? 0,

        gain_final:
          p.gain_final ?? null,

        observation:
          p.observation || null,
      }));

      // setPrestations(data);
      setPrestations(sortPrestationsByDate(data));
    }

  } catch (err) {

    console.error(
      'Erreur chargement prestations:',
      err
    );

    toast.error(
      "Impossible de charger les prestations"
    );

  } finally {

    setLoading(false);
  }
};
 const handleAutoCompleteExpired = async (prestationId) => {
  try {
    const res = await axiosInstance.put(
      `/prestations/${prestationId}/auto-complete`
    );

    console.log(
      '✅ Prestation auto-complétée:',
      res.data
    );

    return res.data;

  } catch (err) {
    console.error(
      '❌ Auto-complete échoué:',
      prestationId,
      err.response?.data || err
    );

    return null;
  }
};

  const handleAccept = async (id) => {
    try {
      await axiosInstance.put(`/prestations/${id}/accept`);
      toast.success('✅ Prestation acceptée');
      fetchPrestations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'acceptation");
    }
  };

  const handleOpenExchange = async (prestationId) => {
    try {
      setExchangePrestationId(prestationId);

      const res = await axiosInstance.get(
        `/exchanges/eligible-users?prestation_id=${prestationId}`
      );

      setEligibleUsers(res.data);
      setShowExchangeModal(true);

    } catch (err) {
      toast.error("Erreur chargement soignants");
    }
  };

  const handleRequestExchange = async (targetUserId) => {
    try {
      await axiosInstance.post('/exchanges/request', {
        prestation_id: exchangePrestationId,
        target_user_id: targetUserId
      });

      toast.success("🔁 Demande envoyée");
      setShowExchangeModal(false);

    } catch (err) {
      toast.error("Erreur demande échange");
    }
  };

  // 🔥 NOUVEAU : Enregistrer observation
  const handleSaveObservation = async (id) => {
    try {
      await axiosInstance.put(`/prestations/${id}/observation`, {
        observation: observations[id] || ""
      });

      toast.success('💬 Observation sauvegardée');

      // 🔥 IMPORTANT : resync avec la DB
      await fetchPrestations();

    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur sauvegarde");
    }
  };

  // 🔥 AUTO-COMPLETION pour prestations expirées


  const ROLE_LABELS = {
    caregiver: "Aide-soignant",
    nurse: "Infirmier",
    admin: "Administrateur"
  };

  if (!user) return null;

  return (
    <Layout title="Mes prestations">
      {loading && <p className="text-center text-gray-600 mt-2">Chargement...</p>}
      {!loading && prestations.length === 0 && (
        <p className="text-center text-gray-600 mt-8">Aucune prestation trouvée.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {showExchangeModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-xl w-[90%] max-w-md shadow-xl">

      <h2 className="text-lg font-bold mb-4">
        🔁 Choisir un soignant
      </h2>

      {eligibleUsers.length === 0 && (
        <p className="text-sm text-gray-500">Aucun soignant disponible</p>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {eligibleUsers.map((u) => (
          <div
            key={u.user_id}
            className="p-3 border rounded-lg flex justify-between items-center"
          >
            <div>
              <p className="font-semibold">{u.name}</p>
              {/* <p className="text-xs text-gray-500">{u.role}</p> */}
              <p className="text-xs text-gray-500">
                {ROLE_LABELS[u.role] || u.role}
              </p>
            </div>

            <Button
              onClick={() => handleRequestExchange(u.user_id)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Choisir
            </Button>
          </div>
        ))}
      </div>

      <Button
        onClick={() => setShowExchangeModal(false)}
        className="mt-4 w-full bg-gray-500 hover:bg-gray-600 text-white"
      >
        Fermer
      </Button>

    </div>
  </div>
)}
        {prestations.map((p) => {
          const status = p.status || 'pending';
          const planned = p.dureeMinutes || 0;

          // Le statut backend indique si la prestation est réellement terminée
          const displayCompleted = status === 'completed';

          // Sert uniquement pour savoir si la date prévue est dépassée
          const isExpiredPrestation =
            p.dateEnd &&
            new Date(p.dateEnd) < new Date();

          return (
          <Card
      key={p._id || p.id}
      className="glass-card border-amber-200 hover:shadow-lg transition"
    >
      <CardHeader>
        <CardTitle className="text-lg text-amber-900 font-semibold">
          {p.name}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-2 text-gray-700">
        <p className="text-sm text-gray-600">{p.description}</p>

        {p.etablissement && (
          <div className="flex flex-col gap-1 text-sm mt-2">
            <span className="flex items-center gap-2">
              <Building2 size={18} className="text-amber-600" />
              {p.etablissement.name}
            </span>
            <span className="text-gray-600">
              Adresse : {p.etablissement.address}
            </span>
            <span className="text-gray-600">
              Tel : {p.etablissement.phone}
            </span>
          </div>
        )}

        <div className="mt-2">
          <span className="flex items-center gap-2 block text-sm">
            <Calendar size={18} className="text-amber-600 inline" />
            Début prévu : {fmt(p.dateStart)}
          </span>
          <span className="flex items-center gap-2 block text-sm">
            <Calendar size={18} className="text-amber-600 inline" />
            Fin prévue : {fmt(p.dateEnd)}
          </span>
          <span className="flex items-center gap-2 block text-sm">
            <Clock size={18} className="text-amber-600 inline" />
            Durée prévue : {planned} min
          </span>
        </div>

        {/* 🔁 BOUTON ÉCHANGE */}
        {status !== 'completed' && (
          <div className="mt-3">
            <Button
              onClick={() => handleOpenExchange(p._id)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              🔁 Demander un échange
            </Button>
          </div>
        )}

        {/* 🔥 STATUS + GAIN UNIFIÉ */}
        <div className="mt-3 space-y-3">
          {/* Status */}
          <div className="text-center">
            <span
  className={`px-4 py-2 rounded-full text-xs font-bold shadow-md ${
    displayCompleted
      ? 'bg-green-100 text-green-800 border-2 border-green-200'
      : status === 'accepted'
        ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-200'
        : 'bg-gray-100 text-gray-800 border border-gray-200'
  }`}
>
  {displayCompleted
    ? 'COMPLETED'
    : status.toUpperCase()}
</span>
          </div>

          {/* 🔥 GAIN : Unifié + Auto-coloré */}
          <div
  className={`p-4 rounded-xl shadow-md flex items-center justify-between ${
    displayCompleted
      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200'
      : 'bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200'
  }`}
>
  <div className="flex flex-col">
    <div className="flex items-center gap-2 mb-1">
      <Euro
        size={20}
        className={
          displayCompleted
            ? 'text-green-700'
            : 'text-amber-800'
        }
      />

      <span
        className={`font-bold text-sm ${
          displayCompleted
            ? 'text-green-900'
            : 'text-amber-900'
        }`}
      >
        {displayCompleted ? 'GAIN FINAL' : 'GAIN ESTIMÉ'}
      </span>
    </div>

    {displayCompleted && (
      <span className="text-xs text-green-600 font-medium">
        Prestation terminée
      </span>
    )}
  </div>

  <span
    className={`text-2xl font-black ${
      displayCompleted
        ? 'text-green-900 drop-shadow-md'
        : 'text-amber-900'
    }`}
  >
    {(
      displayCompleted
        ? Number(p.gain_final ?? p.final_rate ?? 0)
        : Number(p.final_rate ?? p.gain_estime ?? 0)
    ).toFixed(2)} €
  </span>
</div>

          {/* Tarification */}
          <p className="text-xs text-center text-gray-600 px-2 py-1 bg-gray-50 rounded">
            {p.pricing_type?.toLowerCase() === 'pourcentage' ? '🔥 Majoré (pourcentage)' : '💰 Tarif fixe'}
          </p>
        </div>

        {/* 🔥 OBSERVATIONS */}
        {(status === 'accepted' || status === 'in_progress' || isExpiredPrestation) && (
          <div className="mt-4 space-y-2 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
            <label className="block text-sm font-semibold text-purple-900">
              💬 Observation
            </label>
            <textarea
              placeholder="Décrivez le déroulement de la prestation..."
              className="w-full p-3 border-2 border-purple-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"

              value={observations[p._id] ?? p.observation ?? ""}
                onChange={(e) =>
                  setObservations((prev) => ({
                    ...prev,
                    [p._id]: e.target.value
                  }))
                }
            />
            <Button
              onClick={() => handleSaveObservation(p.id || p._id)}
              className="bg-gradient-to-r from-orange-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white w-full font-semibold shadow-lg"
            >
              💾 Sauvegarder
            </Button>
          </div>
        )}

        {/* Observation lue seule */}
        {status === 'completed' && p.observation && (
          <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200 shadow-sm">
            <div className="font-bold text-purple-900 text-sm mb-2 flex items-center gap-2">
              📝 Mon observation
            </div>
            <p className="text-sm text-purple-800 whitespace-pre-line bg-white p-3 rounded-lg">
              {p.observation}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
})}
      </div>
    </Layout>
  );
}